import { AnimatedCounter } from "@/components/company/company-animations";
import FinancialMetricTile from "@/components/company/financial-metric-tile";
import { useTranslation } from "@/hooks/useTranslation";
import { computeYoy, formatCompactAmount } from "@/lib/format-amount";
import type { CompanyDTO } from "@shaarp/common";
import {
  BarChart3Icon,
  CircleDollarSignIcon,
  TrendingUpIcon,
  WalletIcon,
} from "lucide-react";

type CardFinancialsSummaryProps = {
  company: CompanyDTO;
};

type SummaryMetric = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  year: number | null;
  value: number | null;
  yoyPercentage: number | null;
};

/** The headline figures of the last accounts, as bare tiles of a `FinancialMetricGrid`. */
export default function CardFinancialsSummary({
  company,
}: CardFinancialsSummaryProps) {
  const { t } = useTranslation();
  const finances = company.business.finances;
  const latest = finances[0];
  const previous = finances[1];

  if (!latest) return null;

  const metrics: SummaryMetric[] = [
    {
      icon: BarChart3Icon,
      label: t("company.insights.financials.revenue"),
      year: latest.year,
      value: latest.sales ?? null,
      yoyPercentage:
        latest.sales != null
          ? (latest.revenueGrowthRate ??
            computeYoy(latest.sales, previous?.sales))
          : null,
    },
    {
      icon: CircleDollarSignIcon,
      label: t("company.insights.financials.netResult"),
      year: latest.year,
      value: latest.result ?? null,
      yoyPercentage:
        latest.result != null
          ? computeYoy(latest.result, previous?.result)
          : null,
    },
    {
      icon: TrendingUpIcon,
      label: "EBITDA",
      year: latest.year,
      value: latest.ebitda ?? null,
      yoyPercentage:
        latest.ebitda != null
          ? computeYoy(latest.ebitda, previous?.ebitda)
          : null,
    },
    {
      icon: WalletIcon,
      label: t("company.insights.financials.grossMargin"),
      year: latest.year,
      value: latest.grossMargin ?? null,
      yoyPercentage:
        latest.grossMargin != null
          ? computeYoy(latest.grossMargin, previous?.grossMargin)
          : null,
    },
  ];

  const visibleMetrics = metrics.filter((m) => m.value != null);

  if (visibleMetrics.length === 0) return null;

  return (
    <>
      {visibleMetrics.map((metric) => (
        <FinancialMetricTile
          key={metric.label}
          icon={metric.icon}
          label={metric.label}
          year={metric.year}
          yoyPercentage={metric.yoyPercentage}
          value={
            <AnimatedCounter
              value={metric.value!}
              formatFn={formatCompactAmount}
            />
          }
        />
      ))}
    </>
  );
}
